// Approximate USD → local currency rates for the DISPLAY-ONLY badge shown next
// to the actual Stripe charge (which is always in USD). These rates are a
// static reference snapshot — refresh manually every few months. The badge
// visibly says "≈" so customers understand this is an estimate, not the
// exact amount they will be charged. Keeping this static avoids adding a
// third-party FX API dependency to the checkout path (a hard-critical path).
//
// Structure:
//   {
//     "SAR": { rate: 3.75, symbolLocale: "ar-SA" },
//     ...
//   }
//   rate = 1 USD in target currency; symbolLocale is passed to Intl.NumberFormat
//   so the badge is formatted with the correct symbol / digit system for RTL
//   locales.
//
// Coverage: the 13 shipped UI locales' most common home currency.
// Fallback: no entry → do not render the badge (the USD price already shows).

const RATES = {
  // Middle East / North Africa
  SAR: { rate: 3.75, symbolLocale: "ar-SA" },
  AED: { rate: 3.67, symbolLocale: "ar-AE" },
  EGP: { rate: 48.7, symbolLocale: "ar-EG" },
  TRY: { rate: 34.3, symbolLocale: "tr-TR" },
  // Europe
  EUR: { rate: 0.92, symbolLocale: "de-DE" },
  GBP: { rate: 0.78, symbolLocale: "en-GB" },
  RUB: { rate: 93.0, symbolLocale: "ru-RU" },
  // South Asia
  INR: { rate: 83.5, symbolLocale: "hi-IN" },
  PKR: { rate: 278.0, symbolLocale: "ur-PK" },
  BDT: { rate: 119.0, symbolLocale: "bn-BD" },
  // East Asia
  CNY: { rate: 7.15, symbolLocale: "zh-CN" },
  JPY: { rate: 149.0, symbolLocale: "ja-JP" },
  KRW: { rate: 1370.0, symbolLocale: "ko-KR" },
  // Americas
  BRL: { rate: 5.4, symbolLocale: "pt-BR" },
  MXN: { rate: 19.5, symbolLocale: "es-MX" },
};

// Locale → preferred currency mapping. This is a heuristic — customers can
// browse in Arabic from anywhere in the world, but the majority signal is
// still the language they chose. Kept as a plain lookup so it's obvious what
// each language will show.
const LANG_TO_CCY = {
  en: null,          // USD-native → no badge (would be redundant)
  es: "EUR",
  fr: "EUR",
  de: "EUR",
  pt: "EUR",
  it: "EUR",
  hi: "INR",
  ar: "SAR",
  ur: "PKR",
  zh: "CNY",
  ja: "JPY",
  tr: "TRY",
  ru: "RUB",
};

// Business-timezone → currency mapping. When the caller passes a business
// timezone, this takes PRIORITY over the language-based guess above so that
// (e.g.) a Saudi-based business browsing in Urdu still sees SAR instead of
// PKR. Only zones whose currency is already in RATES are listed — anything
// unknown falls through to the language default.
const TZ_TO_CCY = {
  "Asia/Riyadh": "SAR",
  "Asia/Dubai": "AED",
  "Asia/Muscat": "AED",
  "Asia/Qatar": "SAR",
  "Asia/Bahrain": "SAR",
  "Asia/Kuwait": "SAR",
  "Asia/Aden": "SAR",
  "Africa/Cairo": "EGP",
  "Europe/Istanbul": "TRY",
  "Europe/Moscow": "RUB",
  "Asia/Kolkata": "INR",
  "Asia/Calcutta": "INR",
  "Asia/Karachi": "PKR",
  "Asia/Dhaka": "BDT",
  "Asia/Shanghai": "CNY",
  "Asia/Tokyo": "JPY",
  "Asia/Seoul": "KRW",
  "America/Sao_Paulo": "BRL",
  "America/Mexico_City": "MXN",
  // Europe → EUR (most eurozone zones)
  "Europe/Paris": "EUR",
  "Europe/Berlin": "EUR",
  "Europe/Madrid": "EUR",
  "Europe/Rome": "EUR",
  "Europe/Lisbon": "EUR",
  "Europe/Amsterdam": "EUR",
  "Europe/Brussels": "EUR",
  "Europe/Vienna": "EUR",
  "Europe/Dublin": "EUR",
  "Europe/Athens": "EUR",
  "Europe/Helsinki": "EUR",
  // UK
  "Europe/London": "GBP",
};

/**
 * Return a display-only "≈ SAR 371" style string for the given USD amount +
 * UI language, or `null` when no meaningful conversion is available (English,
 * unknown currency, invalid amount). Stripe still charges the USD amount; this
 * is purely informational.
 *
 * When `businessTimeZone` is supplied AND it maps to a known currency, the
 * business-timezone currency takes priority over the language guess. This is
 * how a Saudi business browsing in Urdu correctly shows SAR (via Asia/Riyadh
 * → SAR) instead of PKR from the language default. The digit system + symbol
 * still respect the CUSTOMER's language (e.g., Arabic-Indic numerals for `ar`,
 * Urdu numerals for `ur`) so the badge reads natively for every visitor.
 */
export function formatLocalCurrencyBadge(usdAmount, lang, businessTimeZone) {
  // English (USD-native) speakers never see a local badge — even for a Saudi
  // business, the "≈ SAR 371" line would be more confusing than helpful when
  // the customer already reads USD prices natively.
  if (lang && LANG_TO_CCY[lang] === null) return null;
  // 1. Business-timezone override (highest priority — reflects where the
  //    money is actually being spent). Only applies when we recognize the tz.
  let ccyCode = businessTimeZone ? TZ_TO_CCY[businessTimeZone] : null;
  // 2. Fall through to language guess when no tz match.
  if (!ccyCode) ccyCode = LANG_TO_CCY[lang];
  if (!ccyCode) return null;
  const entry = RATES[ccyCode];
  if (!entry) return null;
  const amt = Number(usdAmount);
  if (!Number.isFinite(amt) || amt <= 0) return null;
  const converted = amt * entry.rate;
  // Prefer the CUSTOMER's language locale (RTL digits, comma vs dot, etc.)
  // but the CURRENCY code is decided above. This gives Urdu customers of a
  // Saudi business "‎‎‏‎‏‎‎‎‏‎‎‏‏‎‎‏‎ر.س.‎‏‎‎‏‏‎‎ ٣٧١" — Saudi currency but
  // formatted with the reader's own digit system.
  const digitLocale = entry.symbolLocale;
  try {
    const formatted = new Intl.NumberFormat(digitLocale, {
      style: "currency",
      currency: ccyCode,
      maximumFractionDigits: converted >= 100 ? 0 : 2,
    }).format(converted);
    return `\u2248 ${formatted}`; // ≈ prefix so it reads as an estimate
  } catch {
    return null;
  }
}
